/**
 * InterviewPrepViewer.jsx — Reusable 5-bucket Interview Prep render
 * (RC 1.52 Slice 3A, operator-locked 2026-05-11 PM).
 *
 * Extracted verbatim from `components/workspace/InterviewPrepSection.jsx`
 * (lines ~988-1160 of the pre-3A file). Pure render. Reads ONE prop:
 *   • interviewPrep — raw payload from `result.interview_prep` OR
 *                     `content_json.interview_prep` (Library lane).
 *
 * Renders nothing else — no header / footer / action surface. Callers
 * are responsible for the chrome (banner, modal, generation footer).
 *
 * The `mapInterviewPrep` helper handles null / partial / legacy
 * payloads, returning a 5-bucket skeleton with `isLegacyOrEmpty=true`.
 * The viewer renders an empty-state card in that case.
 *
 * Hard rails (Slice 3A):
 *  • No fetch. No side effects.
 *  • No data-testid renames — the existing `workspace-fit-prep-*`
 *    test IDs are preserved verbatim so existing tests pass.
 *  • Same Tailwind classes / Lucide icons / Badge component as the
 *    section. Visual diff vs. the workspace render = zero.
 */

import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { mapInterviewPrep, INTERVIEW_PREP_BUCKET_KEYS } from '@/lib/interviewPrepShape';
import { stripMarkdownTokens } from '@/lib/markdownStrip';

export default function InterviewPrepViewer({ interviewPrep }) {
  const effective = useMemo(
    () => mapInterviewPrep(interviewPrep || null),
    [interviewPrep],
  );

  if (effective.isLegacyOrEmpty) {
    return (
      <div
        data-testid="workspace-fit-prep-empty"
        className="rounded-md border border-dashed border-border/60 bg-background/50 p-3 text-[11px] text-ndor-ink-muted leading-relaxed"
      >
        <p>
          Interview prep content is not available for this run.
          Generate a fresh run to surface tailored questions and
          guidance.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="workspace-fit-prep-buckets"
      className="space-y-3 pt-1"
    >
      {effective.headline && (
        <p
          data-testid="workspace-fit-prep-headline"
          className="text-[12px] text-ndor-ink leading-relaxed font-medium"
        >
          {stripMarkdownTokens(effective.headline)}
        </p>
      )}

      {effective.preparationPriorities.length > 0 && (
        <div
          data-testid="workspace-fit-prep-priorities"
          className="rounded-md border border-border/40 bg-ndor-gold/[0.04] p-2.5 space-y-1"
        >
          <p className="text-[10px] uppercase tracking-[0.14em] text-ndor-gold font-semibold">
            Top priorities
          </p>
          <ul className="text-[12px] text-ndor-ink leading-relaxed space-y-1 list-none">
            {effective.preparationPriorities.map((p, idx) => (
              <li
                key={idx}
                data-testid={`workspace-fit-prep-priority-${idx}`}
                className="flex gap-1.5"
              >
                <span className="text-ndor-gold shrink-0">•</span>
                <span>{stripMarkdownTokens(p)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {INTERVIEW_PREP_BUCKET_KEYS.map((key) => {
        const bucket = effective.buckets[key];
        const total =
          bucket.questions.length + bucket.areas.length + bucket.notes.length;
        if (total === 0) return null;
        return (
          <details
            key={key}
            data-testid={`workspace-fit-prep-bucket-${key}`}
            className="group rounded-md border border-border/50 bg-background/60 open:bg-background/80 transition"
          >
            <summary
              data-testid={`workspace-fit-prep-bucket-${key}-summary`}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden"
            >
              <span className="text-[12px] font-medium text-ndor-ink flex-1">
                {bucket.label}
              </span>
              <Badge className="text-[10px] tabular-nums bg-ndor-line/40 text-ndor-ink-muted border-0">
                {total}
              </Badge>
              <span
                aria-hidden
                className="text-ndor-ink-muted text-[10px] transition-transform group-open:rotate-90"
              >
                ▶
              </span>
            </summary>

            <div className="px-3 pb-3 pt-1 space-y-2.5 text-[12px] text-ndor-ink leading-relaxed">
              {bucket.questions.length > 0 && (
                <ul
                  data-testid={`workspace-fit-prep-bucket-${key}-questions`}
                  className="space-y-1.5 list-none"
                >
                  {bucket.questions.map((q, idx) => (
                    <li key={idx} className="flex gap-1.5">
                      <span className="text-ndor-gold shrink-0">Q.</span>
                      <span>{stripMarkdownTokens(q.question)}</span>
                    </li>
                  ))}
                </ul>
              )}

              {bucket.areas.length > 0 && (
                <ul
                  data-testid={`workspace-fit-prep-bucket-${key}-areas`}
                  className="space-y-2 list-none"
                >
                  {bucket.areas.map((a, idx) => (
                    <li
                      key={idx}
                      className="rounded-md border border-border/30 bg-background/70 p-2 space-y-0.5"
                    >
                      <p className="font-medium text-ndor-ink">
                        {stripMarkdownTokens(a.area || a.vulnerability || '')}
                      </p>
                      {a.why && (
                        <p className="text-[11px] text-ndor-ink-muted">
                          <span className="font-medium">Why probed:</span>{' '}
                          {stripMarkdownTokens(a.why)}
                        </p>
                      )}
                      {a.focus && (
                        <p className="text-[11px] text-ndor-ink-muted">
                          <span className="font-medium">Prep focus:</span>{' '}
                          {stripMarkdownTokens(a.focus)}
                        </p>
                      )}
                      {a.starAnchor && (
                        <p
                          data-testid={`workspace-fit-prep-bucket-${key}-star-${idx}`}
                          className="text-[11px] text-ndor-ink-muted"
                        >
                          <span className="font-medium">STAR anchor:</span>{' '}
                          {stripMarkdownTokens(a.starAnchor)}
                        </p>
                      )}
                      {a.framing && (
                        <p className="text-[11px] text-ndor-ink-muted">
                          <span className="font-medium">Likely framing:</span>{' '}
                          {stripMarkdownTokens(a.framing)}
                        </p>
                      )}
                      {a.strategy && (
                        <p className="text-[11px] text-ndor-ink-muted">
                          <span className="font-medium">Strategy:</span>{' '}
                          {stripMarkdownTokens(a.strategy)}
                        </p>
                      )}
                      {a.soften && (
                        <p className="text-[11px] text-ndor-ink-muted">
                          <span className="font-medium">Soften:</span>{' '}
                          {stripMarkdownTokens(a.soften)}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {bucket.notes.length > 0 && (
                <ul
                  data-testid={`workspace-fit-prep-bucket-${key}-notes`}
                  className="space-y-1 list-none"
                >
                  {bucket.notes.map((n, idx) => (
                    <li key={idx} className="flex gap-1.5">
                      <span className="text-ndor-gold shrink-0">•</span>
                      <span>{stripMarkdownTokens(n)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </details>
        );
      })}
    </div>
  );
}
